import { useState } from "react";
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import { Label } from "@/project/karuna/components/ui/label"; // @ts-ignore - label is not a valid module
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/project/karuna/components/ui/input-otp"; // @ts-ignore - input-otp is not a valid module
import { useNavigate } from "react-router-dom";
import { GraduationCap, ArrowLeft, Loader2 } from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { z } from "zod";
import navigationRoutes, {
  useKarunaLink,
} from "@/project/karuna/utils/navigationRoutes";
import OTPService from "@/services/APIs/OTPService";
import { TenantUserService } from "@/services/APIs";
import { SCHOOL_DEFAULT_PASSWORD } from "@/project/karuna/utils/schoolTenantUser";
import {
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
} from "@/lib/storeManager";

// Helper function to normalize mobile number (remove spaces, ensure 10 digits)
const normalizeMobileNumber = (mobileNumber: string): string => {
  return mobileNumber.replace(/\D/g, "").slice(0, 10);
};

const authSchema = z.object({
  schoolName: z
    .string()
    .trim()
    .min(2, { message: "School name must be at least 2 characters" })
    .max(200, { message: "School name too long" }),
  mobileNumber: z
    .string()
    .trim()
    .regex(/^[6-9]\d{9}$/, {
      message: "Invalid mobile number. Must be 10 digits starting with 6-9",
    }),
  otp: z
    .string()
    .length(6, { message: "OTP must be 6 digits" })
    .regex(/^\d{6}$/, { message: "OTP must contain only digits" }),
});

const SchoolAuth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const getLink = useKarunaLink();
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  const [formData, setFormData] = useState({
    schoolName: "",
    mobileNumber: "",
    otp: "",
  });
  const [codeReference, setCodeReference] = useState<string | null>(null);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  const handleSendOTP = async () => {
    // Prevent multiple simultaneous checks
    if (isCheckingDuplicate || loading) {
      return;
    }

    setLoading(true);
    setIsCheckingDuplicate(true);

    // STEP 1: Validate form data
    try {
      z.object({
        mobileNumber: z
          .string()
          .trim()
          .regex(/^[6-9]\d{9}$/, {
            message:
              "Invalid mobile number. Must be 10 digits starting with 6-9",
          }),
        schoolName: z
          .string()
          .trim()
          .min(2, { message: "School name must be at least 2 characters" }),
      }).parse({
        mobileNumber: formData.mobileNumber,
        schoolName: formData.schoolName,
      });
    } catch (validationError: any) {
      if (validationError instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: validationError.issues[0]?.message ?? "Invalid input",
          variant: "destructive",
        });
      }
      setLoading(false);
      return;
    }

    // Send OTP for signup (POST /api/auth/)
    const normalizedMobile = normalizeMobileNumber(formData.mobileNumber);
    try {
      // Phone code UUID from env (same as used in Postman for karunaapi)
      const trimmed = (import.meta.env.VITE_OTP_PHONE_CODE ?? "")
        .replace(/^"|"$/g, "")
        .trim();
      const phoneCode =
        trimmed !== "" ? trimmed : "73835e38-252c-4232-9b5d-474efc1fb2f4";

      // Send OTP via API
      const response = await OTPService.sendOTP(normalizedMobile, phoneCode);

      // Check if response is valid
      if (!response?.message) {
        throw new Error("Failed to send OTP. Please try again.");
      }

      // Store code_reference for verification - MUST use this exact code_reference
      if (response.code_reference) {
        setCodeReference(response.code_reference);
        console.log("✅ Stored code_reference:", response.code_reference);
      } else {
        throw new Error("No code_reference received from server");
      }

      setOtpSent(true);
      setOtpTimer(60); // 60 seconds timer

      // Start countdown timer
      const interval = setInterval(() => {
        setOtpTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      toast({
        title: "OTP Sent",
        description: response.message,
      });
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.issues[0]?.message ?? "Invalid mobile number",
          variant: "destructive",
        });
      } else {
        // Check for "phone number already exists" error from backend OTP service
        const errorData = error?.response?.data;
        const errorMessageStr =
          errorData?.error?.message ??
          errorData?.message ??
          errorData?.detail ??
          "";

        // If backend says user/phone already exists, redirect to login
        const errLower =
          typeof errorMessageStr === "string"
            ? errorMessageStr.toLowerCase()
            : "";
        if (
          errLower &&
          (errLower.includes("phone number already exists") ||
            errLower.includes("phone_number_exists") ||
            errLower.includes("already registered") ||
            errLower.includes("already exists") ||
            errLower.includes("user already exists"))
        ) {
          console.log("⚠️ Backend says phone number already exists");
          toast({
            title: "Already Registered",
            description:
              "This mobile number is already registered. Please login instead.",
            variant: "destructive",
          });

          setTimeout(() => {
            navigate(getLink("/school/login"));
          }, 2000);

          setLoading(false);
          setIsCheckingDuplicate(false);
          return;
        } else {
          // Other errors - show generic error message
          let errorMessage =
            error.message ?? "Failed to send OTP. Please try again.";

          if (errorData) {
            if (errorData?.error?.message) {
              errorMessage = errorData.error.message;
            } else if (errorData?.message) {
              errorMessage = errorData.message;
            } else if (errorData?.detail) {
              errorMessage = errorData.detail;
            }
          }

          toast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
          });
        }
      }
    } finally {
      setLoading(false);
      setIsCheckingDuplicate(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!otpSent) {
        throw new Error("Please send OTP first");
      }

      // Validate form data
      authSchema.parse(formData);

      // Verify OTP via Django API - only need otp and code_reference
      if (!codeReference) {
        throw new Error("Code reference is missing. Please send OTP again.");
      }

      console.log("🔑 Using code_reference for verification:", codeReference);
      console.log("🔑 OTP being verified:", formData.otp);

      const response = await OTPService.verifyOTP(formData.otp, codeReference);

      // Check if response is valid
      if (!response?.access) {
        throw new Error(
          "Invalid OTP or authentication failed. Please try again."
        );
      }

      // Store authentication tokens
      if (response.access) {
        setLocalAccessToken(response.access);
      }
      if (response.refresh) {
        setLocalRefreshToken(response.refresh);
      }
      if (response.user) {
        // Store user info along with school name from signup form
        const userInfoToStore = {
          ...response.user,
          signup_school_name: formData.schoolName, // Store school name from signup
          phone_number: normalizeMobileNumber(formData.mobileNumber), // Store normalized mobile number
        };
        console.log("💾 Storing user info with signup data:", userInfoToStore);
        setLocalUserInfo(userInfoToStore);
        console.log("✅ User info stored successfully");
      }

      // Create or update user in architecture-one's /api/users/ system
      // This ensures user has architecture-one credentials to access schemas
      try {
        const userEmail = response.user?.email;
        const normalizedMobile = normalizeMobileNumber(formData.mobileNumber);
        const schoolName = formData.schoolName?.trim() || "";

        if (!userEmail) {
          console.warn(
            "⚠️ No email in OTP response, cannot create user in /api/users/"
          );
        } else {
          console.log(
            "💾 Creating/updating user in architecture-one /api/users/ system..."
          );
          console.log("📱 Phone number to store:", normalizedMobile);
          console.log("🏫 School name to store:", schoolName);

          // Check if user already exists
          let existingUser: any = null;
          try {
            const usersResponse = await TenantUserService.listTenantUsers(
              1,
              100
            );
            const usersList =
              usersResponse?.users ?? usersResponse?.results ?? [];

            if (Array.isArray(usersList)) {
              // Try to find by phone number first
              existingUser = usersList.find((user: any) => {
                const userPhone = normalizeMobileNumber(
                  user.phone_number ?? ""
                );
                return userPhone === normalizedMobile && userPhone.length > 0;
              });

              // If not found by phone, try by email
              existingUser ??= usersList.find(
                (user: any) =>
                  user.email?.toLowerCase() === userEmail.toLowerCase()
              );
            }
          } catch (checkError) {
            console.warn("⚠️ Could not check if user exists:", checkError);
          }

          // Prepare user data for /api/users/ API
          // CRITICAL: Always store school name in first_name
          const userPayload: any = {
            email: userEmail,
            password: SCHOOL_DEFAULT_PASSWORD,
            phone_number: normalizedMobile, // CRITICAL: Store phone number for lookup
            first_name: schoolName, // CRITICAL: Always store school name in first_name
          };

          // Add school_name field if API supports it
          if (schoolName) {
            userPayload.school_name = schoolName;
          }

          // Add other optional fields from OTP response (only if school name is not set)
          if (!schoolName && response.user?.first_name) {
            userPayload.first_name = response.user.first_name;
          }
          if (response.user?.last_name) {
            userPayload.last_name = response.user.last_name;
          }
          if (response.user?.full_name) {
            userPayload.full_name = response.user.full_name;
          }

          console.log("📋 User payload for /api/users/:", userPayload);
          console.log("📋 School name in first_name:", userPayload.first_name);

          try {
            if (existingUser) {
              // User exists - update with school name
              console.log(
                "🔄 User already exists, updating with school name..."
              );
              const updatePayload: any = {
                first_name: schoolName ?? existingUser.first_name ?? "",
                phone_number: normalizedMobile,
              };

              // Only update if school name is provided
              if (schoolName) {
                updatePayload.first_name = schoolName;
                if (updatePayload.school_name !== undefined) {
                  updatePayload.school_name = schoolName;
                }
              }

              await TenantUserService.updateUser(
                existingUser.id,
                updatePayload
              );
              console.log(
                "✅ User updated in /api/users/ with school name:",
                schoolName
              );
            } else {
              // User doesn't exist - create new
              const userResponse =
                await TenantUserService.createUser(userPayload);
              console.log(
                "✅ User created in /api/users/ successfully:",
                userResponse
              );
              console.log(
                "✅ School name stored in first_name:",
                userResponse.first_name
              );
            }
          } catch (createError: any) {
            // If user already exists (duplicate email), try to update instead
            const errorMessage =
              createError?.response?.data?.detail ??
              createError?.response?.data?.message ??
              createError?.message ??
              "";

            console.error("❌ Error creating/updating user:", createError);
            console.error("❌ Error response:", createError?.response?.data);

            if (
              errorMessage.toLowerCase().includes("already exists") ||
              errorMessage.toLowerCase().includes("duplicate") ||
              errorMessage.toLowerCase().includes("unique") ||
              createError?.response?.status === 400
            ) {
              console.log("ℹ️ User already exists, attempting to update...");
              // Try to find and update the existing user
              try {
                const usersResponse = await TenantUserService.listTenantUsers(
                  1,
                  100
                );
                const usersList =
                  usersResponse?.users ?? usersResponse?.results ?? [];
                const existingUser = usersList.find(
                  (user: any) =>
                    user.email?.toLowerCase() === userEmail.toLowerCase()
                );

                if (existingUser && schoolName) {
                  await TenantUserService.updateUser(existingUser.id, {
                    first_name: schoolName,
                    phone_number: normalizedMobile,
                  } as any);
                  console.log(
                    "✅ Updated existing user with school name:",
                    schoolName
                  );
                }
              } catch (updateError) {
                console.warn("⚠️ Could not update existing user:", updateError);
              }
            } else {
              // Log other errors but don't block registration
              console.warn(
                "⚠️ Could not create user in /api/users/:",
                createError
              );
              console.warn(
                "⚠️ User can still proceed - OTP authentication succeeded"
              );
            }
          }
        }
      } catch (userError: any) {
        // Don't block registration if /api/users/ call fails
        // User already has OTP tokens, so they can proceed
        console.error("⚠️ Error creating user in /api/users/:", userError);
        console.warn(
          "⚠️ User can still proceed - OTP authentication succeeded"
        );
      }

      toast({
        title: "Registration Successful",
        description: "Welcome! Redirecting to profile...",
      });
      navigate(getLink("/school/profile"));
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.issues[0]?.message ?? "Please check your inputs",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message ?? "An error occurred. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto px-4 py-6">
        <Button
          variant="ghost"
          onClick={() => navigate(getLink(navigationRoutes.DEMO_LOGIN))}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Button>
      </div>

      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md border-border shadow-strong">
          <CardHeader className="space-y-4 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-hero">
              <GraduationCap className="h-10 w-10 text-primary-foreground" />
            </div>
            <div>
              <CardTitle className="text-2">School Signup</CardTitle>
              <CardDescription className="mt-2 text-base">
                Signup to your school account to access the Karuna platform
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="schoolName">
                  School Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="schoolName"
                  type="text"
                  placeholder="Enter your school name"
                  value={formData.schoolName}
                  onChange={e =>
                    setFormData({ ...formData, schoolName: e.target.value })
                  }
                  required
                  disabled={otpSent}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobileNumber">
                  Mobile Number <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="mobileNumber"
                    type="tel"
                    placeholder="9876543210"
                    value={formData.mobileNumber}
                    onChange={e => {
                      // Only allow digits
                      const value = e.target.value.replace(/\D/g, "");
                      const normalized = value.slice(0, 10);
                      setFormData({
                        ...formData,
                        mobileNumber: normalized,
                      });
                    }}
                    onKeyDown={e => {
                      // Allow: backspace, delete, tab, escape, enter
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "Escape",
                          "Enter",
                        ].includes(e.key)
                      ) {
                        return;
                      }
                      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                      if (
                        e.ctrlKey &&
                        ["a", "c", "v", "x"].includes(e.key.toLowerCase())
                      ) {
                        return;
                      }
                      // Allow: Arrow keys, Home, End
                      if (
                        [
                          "ArrowLeft",
                          "ArrowRight",
                          "ArrowUp",
                          "ArrowDown",
                          "Home",
                          "End",
                        ].includes(e.key)
                      ) {
                        return;
                      }
                      // Block everything else that's not a digit
                      if (!/^\d$/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={e => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData("text");
                      const digitsOnly = pastedText
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      setFormData({
                        ...formData,
                        mobileNumber: digitsOnly,
                      });
                    }}
                    required
                    disabled={otpSent}
                    maxLength={10}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSendOTP}
                    disabled={
                      loading ||
                      isCheckingDuplicate ||
                      otpSent ||
                      !formData.mobileNumber ||
                      formData.mobileNumber.length !== 10 ||
                      otpTimer > 0
                    }
                    className="whitespace-nowrap"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : otpTimer > 0 ? (
                      `Resend (${otpTimer}s)`
                    ) : (
                      "Send OTP"
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  10-digit mobile number starting with 6-9
                </p>
              </div>

              {otpSent && (
                <div className="space-y-2">
                  <Label htmlFor="otp">Enter OTP *</Label>
                  <InputOTP
                    maxLength={6}
                    value={formData.otp}
                    onChange={value => {
                      // Only allow digits
                      const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
                      setFormData({ ...formData, otp: digitsOnly });
                    }}
                    pattern="^[0-9]*$"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                  <p className="text-xs text-muted-foreground">
                    Enter the 6-digit OTP sent to {formData.mobileNumber}
                  </p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full border-0 bg-gradient-hero"
                disabled={loading || !otpSent || formData.otp.length !== 6}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Register School"
                )}
              </Button>
            </form>

            <div className="mt-6 space-y-4">
              <div className="rounded-lg bg-muted/50 p-4">
                <p className="text-center text-sm text-muted-foreground">
                  Complete your school profile after registration to start
                  submitting activities
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  Already have an account?{" "}
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-primary underline"
                    onClick={() => navigate(getLink("/school/login"))}
                  >
                    Login here
                  </Button>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SchoolAuth;
